<template>
  <div class="page container p-4 py-4" :style="pageStyle">
    <div v-if="loading || !event">
      <FullScreenLoader :loading="true" />
    </div>
    <div v-else>

      <!-- Banner: dono com evento em rascunho — mensagem dinâmica -->
      <div v-if="ownerDraftBanner" class="owner-draft-warning mb-3" role="alert">
        <i class="fas fa-circle-exclamation flex-shrink-0 mt-1"></i>
        <span class="small">
          {{ ownerDraftBanner.message }}
          <router-link
            v-if="ownerDraftBanner.link"
            :to="ownerDraftBanner.link"
            class="alert-link fw-semibold ms-1"
          >{{ ownerDraftBanner.linkText }}</router-link>
        </span>
      </div>

      <!-- Botão de personalização — visível apenas para o dono -->
      <button
        v-if="isOwner"
        class="customize-fab d-none d-md-inline-flex"
        @click="showCustomizeSidebar = true"
        aria-label="Personalizar página do evento"
      >
        <i class="fas fa-palette"></i>
        <span class="customize-fab__label">Personalizar</span>
      </button>

      <CartHeader :event="event" share="true" title="Carrinho de Presentes" :rsvpConfirmed="rsvpConfirmed" :showInviteButton="true" @invite-click="openConvite" />

      <div
        class="event-content-shell"
        :class="{ 'event-content-shell--unavailable': isEventUnavailableByOwnerActivation }"
        id="gift-list"
      >
        <div class="event-content-shell__content pt-3 py-5">
          <GiftMasonry :event="event" :columns="desktopColumns" />
        </div>

        <div v-if="isEventUnavailableByOwnerActivation" class="event-unavailable-overlay" role="status" aria-live="polite">
          <div class="event-unavailable-overlay__message">
            <div class="event-unavailable-overlay__icon-wrap">
              <div class="event-unavailable-overlay__icon">
                <i class="fas fa-hourglass-half"></i>
              </div>
            </div>
            <p class="event-unavailable-overlay__eyebrow">Lista temporariamente indisponível</p>
            <h4 class="event-unavailable-overlay__title">Este evento estará disponível em breve</h4>
            <p class="event-unavailable-overlay__description">
              O organizador está concluindo a ativação da conta. Assim que finalizar, a lista de presentes ficará disponível para compras.
            </p>
            <button class="btn event-unavailable-overlay__button" @click="voltarHome">
              Entendi
            </button>
          </div>
        </div>
      </div>

      <!-- Rodapé de marca — oculto quando white label está ativo -->
      <footer v-if="!eventStore.whiteLabel" class="event-brand-footer">
        <span>Feito com</span>
        <i class="fas fa-heart event-brand-footer__heart"></i>
        <span>pela equipe <strong>QueMimo</strong></span>
      </footer>

      <FloatingCartButton :eventId="id" v-if="!isEventUnavailableByOwnerActivation" />

      <!-- Botão flutuante de personalização — apenas mobile, apenas dono -->
      <button
        v-if="isOwner"
        class="floating-customize-btn d-md-none"
        :class="{ 'floating-customize-btn--no-cart': !hasCartItems }"
        @click="showCustomizeSidebar = true"
        aria-label="Personalizar evento"
      >
        <i class="fas fa-palette"></i>
      </button>

      <!-- FAB Pix — contribuição livre, visível no mobile para todos -->
      <button
        v-if="!isEventUnavailableByOwnerActivation"
        class="floating-pix-btn d-md-none"
        :class="{
          'floating-pix-btn--has-cart': hasCartItems,
          'floating-pix-btn--with-customize': isOwner,
        }"
        @click="showPixFromList = true"
        aria-label="Contribuir com Pix"
      >
        <i class="fas fa-qrcode"></i>
      </button>
    </div>

    <!-- Sidebar de personalização -->
    <CustomizeSidebar
      v-model="showCustomizeSidebar"
      :eventId="id"
      :activeTheme="activeTheme"
      :saving="themeSaving"
      :saveError="themeSaveError"
      :saved="themeSaved"
      :isPremium="eventCustomization?.is_premium ?? false"
      :customColor="liveCustomColor || eventCustomization?.primary_color || ''"
      :whiteLabel="eventCustomization?.white_label ?? false"
      :bgImageURL="liveBgImage"
      :bgOpacity="liveBgOpacity"
      :croppedBg="sidebarCroppedBg"
      @theme-changed="handleThemeChange"
      @live-color-changed="handleLiveColorChanged"
      @live-bg-changed="handleLiveBgChanged"
      @live-bg-opacity-changed="handleLiveBgOpacityChanged"
      @request-bg-crop="handleBgCropRequest"
      @request-activate-premium="openPremiumModal"
      @live-white-label-changed="(v) => eventStore.setWhiteLabel(v)"
      @premium-settings-saved="onPremiumSettingsSaved"
    />

    <!-- Cropper de imagem de fundo (nível raiz — sem teleport aninhado) -->
    <ImageCropperModal
      :visible="bgCropVisible"
      :src="bgCropSrc"
      :aspect-ratio="16 / 9"
      title="Ajuste a imagem de fundo"
      subtitle="Posicione e enquadre como preferir. Use imagens claras para melhor leitura."
      @confirm="onBgCropConfirm"
      @cancel="bgCropVisible = false"
    />

    <!-- Convite Digital — permanente como shell da página -->
  <ConviteComponent
    v-if="event && !loading"
    :eventName="event?.name || event?.baby_name || ''"
    :eventType="event?.event_type || ''"
    :photos="convitePhotos"
    :eventDate="event?.invite_date ? event.invite_date.slice(0, 10) : ''"
    :eventTime="event?.invite_time || ''"
    :location="event?.invite_location || ''"
    :dressCode="event?.invite_dress_code || ''"
    :primaryColor="resolvedPrimaryColor"
    :bgImage="activeTheme === 'premium' ? (liveBgImage || '') : ''"
    :bgOpacity="liveBgOpacity"
    :whiteLabel="eventStore.whiteLabel"
    :isOwner="isOwner"
    :eventId="event?.id || ''"
    :rsvpCode="route.query.code || ''"
    :hasRsvp="!!event?.has_rsvp"
    :autoOpenPix="showPixFromList"
    :openCartSheet="openCartSheet"
    @close="closeConvite(); showPixFromList = false;"
    @cart-sheet-close="openCartSheet = false"
    @open-customize="showCustomizeSidebar = true"
    @rsvp-confirmed="handleRsvpConfirmed"
  >
    <template #gift-panel>
      <GiftMasonry :event="event" :columns="desktopColumns" />
      <FloatingCartButton v-if="!isEventUnavailableByOwnerActivation" :eventId="id" @open-cart="openCartSheet = true" />
    </template>
  </ConviteComponent>

  <BottomModal v-if="showErrorModal" @close="() => {}">
      <template #default>
        <h5 class="text-center">Erro ao carregar o evento</h5>
        <p class="text-muted text-center">Não foi possível carregar os dados. Verifique sua conexão ou tente novamente mais tarde.</p>
       <div class="d-grid gap-2 mt-4">
        <button class="btn btn-outline-secondary w-100" @click="voltarHome">
          Voltar para o início
        </button>
      </div>
      </template>
    </BottomModal>

    <!-- Modal de Ativação Premium -->
    <BottomModal v-if="showPremiumModal" :zIndex="10200" @close="closePremiumModal">
      <template #default>
        <div class="premium-modal">
          <div class="premium-modal__header">
            <span class="premium-modal__crown"><i class="fas fa-crown"></i></span>
            <h5 class="premium-modal__title">Ativar Premium</h5>
            <p class="premium-modal__subtitle">Personalize as cores e remova a marca QueMimo do seu evento por apenas <strong>R$ 19,90</strong>.</p>
          </div>

          <div v-if="!premiumSuccess">
            <!-- Stripe Card Element -->
            <div class="premium-modal__field">
              <label class="premium-modal__label">Dados do cartão</label>
              <div id="premium-stripe-card-element" class="premium-modal__stripe-element"></div>
              <p v-if="premiumCardError" class="premium-modal__stripe-error">{{ premiumCardError }}</p>
            </div>

            <div class="premium-modal__field">
              <label class="premium-modal__label">Nome no cartão</label>
              <input
                v-model="premiumCardName"
                type="text"
                class="premium-modal__input"
                placeholder="Como aparece no cartão"
              />
            </div>

            <p v-if="premiumError" class="premium-modal__error">
              <i class="fas fa-circle-exclamation me-1"></i>{{ premiumError }}
            </p>

            <p v-if="premiumActivating" class="premium-modal__secure">
              <i class="fas fa-spinner fa-spin me-1"></i> Pagamento aprovado, ativando premium...
            </p>

            <button
              class="premium-modal__pay-btn"
              :disabled="premiumPaying || !premiumCardComplete"
              @click="handleActivatePremium"
            >
              <template v-if="premiumPaying">
                <i class="fas fa-spinner fa-spin me-1"></i> {{ premiumActivating ? 'Ativando premium...' : 'Processando...' }}
              </template>
              <template v-else>
                <i class="fas fa-lock me-1"></i> Pagar R$ 19,90
              </template>
            </button>
            <p class="premium-modal__secure">
              <i class="fas fa-shield-halved me-1"></i> Ambiente 100% seguro e criptografado
            </p>
          </div>

          <!-- Sucesso -->
          <div v-else class="premium-modal__success">
            <i class="fas fa-circle-check premium-modal__success-icon"></i>
            <h6 class="mt-3 mb-1 fw-bold">Premium ativado!</h6>
            <p class="text-muted small">Suas cores e configurações estão aplicadas.</p>
            <button class="premium-modal__close-btn" @click="closePremiumModal">Fechar</button>
          </div>
        </div>
      </template>
    </BottomModal>

  </div>

</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/services/api';
import { patchCustomization, createPremiumCheckout } from '@/services/eventService';
import { loadStripe } from '@stripe/stripe-js';
import GiftMasonry from '@/components/GiftMasonry.vue';
import CartHeader from '@/components/CartHeader.vue';
import CustomizeSidebar from '@/components/CustomizeSidebar.vue';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import PlaySmileBannerSkeleton from '@/components/PlaySmileBannerSkeleton.vue';
import FloatingCartButton from '@/components/FloatingCartButton.vue';
import { useEventStore } from '@/stores/eventStore';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from '@/stores/cartStore';
import BottomModal from '@/components/BottomModal.vue';
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import ConviteComponent from '@/components/ConviteComponent.vue';
import { formatPageTitle, getBrandTitle } from '@/utils/branding';
import { removeThemeFromRoot } from '@/composables/useEventTheme';

// Definição de Props
const props = defineProps({
  id: {
    type: String,
    required: true
  }
});

// Hooks e Funções
const router = useRouter();
const route = useRoute();
const eventStore = useEventStore();
const authStore = useAuthStore();
const cartStore = useCartStore();

// ── Tema e personalização ─────────────────────────────────────
const THEMES = {
  quemimo: { '--event-primary': '#ff50a3', '--event-primary-light': '#fff0f6', '--event-primary-shadow': 'rgba(255, 80, 163, 0.35)', '--event-bg': '#f8f9fa' },
  bebe:    { '--event-primary': '#5b9bd5', '--event-primary-light': '#e8f1fb', '--event-primary-shadow': 'rgba(91, 155, 213, 0.35)', '--event-bg': '#f8f9fa' },
  neutro:  { '--event-primary': '#6b7280', '--event-primary-light': '#f3f4f6', '--event-primary-shadow': 'rgba(107, 114, 128, 0.35)', '--event-bg': '#f8f9fa' },
};

const activeTheme = ref(null);
const liveCustomColor = ref('');
const liveBgImage = ref('');
const liveBgOpacity = ref(0.95); // opacidade da imagem: 1 = totalmente visível, 0 = invisível
const showCustomizeSidebar = ref(false);
const themeSaving = ref(false);
const themeSaveError = ref(false);
const themeSaved = ref(false);
let saveTimer = null;

// ── Cropper de fundo ─────────────────────────────────────────
const bgCropVisible = ref(false);
const bgCropSrc = ref('');
const sidebarCroppedBg = ref(null);

const handleBgCropRequest = (dataUrl) => {
  bgCropSrc.value = dataUrl;
  bgCropVisible.value = true;
};

const onBgCropConfirm = ({ blob, dataUrl }) => {
  bgCropVisible.value = false;
  bgCropSrc.value = '';
  handleLiveBgChanged(dataUrl);
  sidebarCroppedBg.value = { blob, dataUrl };
};

// ── Premium ───────────────────────────────────────────────────
const showPremiumModal  = ref(false);
const premiumPaying     = ref(false);
const premiumActivating = ref(false);
const premiumError      = ref('');
const premiumSuccess    = ref(false);
const premiumCardName   = ref('');
const premiumCardError  = ref('');
const premiumCardComplete = ref(false);
let   _premiumStripe    = null;
let   _premiumCardEl    = null;
let   _premiumStripeKey = '';
const PREMIUM_ACTIVATION_POLL_ATTEMPTS = 10;
const PREMIUM_ACTIVATION_POLL_INTERVAL_MS = 1500;

// Deriva as CSS vars de luz/sombra a partir de uma cor hex
const hexToColorVars = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    light: `rgba(${r}, ${g}, ${b}, 0.08)`,
    shadow: `rgba(${r}, ${g}, ${b}, 0.35)`,
  };
};

const applyThemeToRoot = (theme) => {
  if (theme === 'premium') {
    const color = liveCustomColor.value || eventCustomization.value?.primary_color || '#ff50a3';
    const { light, shadow } = hexToColorVars(color);
    document.documentElement.style.setProperty('--event-primary', color);
    document.documentElement.style.setProperty('--event-primary-light', light);
    document.documentElement.style.setProperty('--event-primary-shadow', shadow);
    document.documentElement.style.setProperty('--event-bg', '#fff');
    return;
  }
  const t = THEMES[theme] || THEMES.quemimo;
  document.documentElement.style.setProperty('--event-primary', t['--event-primary']);
  document.documentElement.style.setProperty('--event-primary-light', t['--event-primary-light']);
  document.documentElement.style.setProperty('--event-primary-shadow', t['--event-primary-shadow']);
  document.documentElement.style.setProperty('--event-bg', t['--event-bg']);
};

watch(activeTheme, (theme) => {
  if (theme) {
    applyThemeToRoot(theme);
    eventStore.setTheme(theme); // persiste no store para as próximas telas
  }
});

const applyBodyBg = (url) => {
  const area = document.querySelector('.main-content-area');
  if (url) {
    const rawAlpha = 1 - liveBgOpacity.value;
    // Limita o overlay a no máximo 72% para evitar fundo excessivamente branco
    const alpha = Math.min(rawAlpha, 0.72).toFixed(2);
    const overlay = `rgba(255,255,255,${alpha})`;
    document.body.style.backgroundImage = `linear-gradient(${overlay}, ${overlay}), url("${url}")`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.minHeight = '100dvh';
    if (area) area.style.backgroundColor = 'transparent';
  } else {
    document.body.style.backgroundImage = '';
    document.body.style.backgroundSize = '';
    document.body.style.backgroundPosition = '';
    document.body.style.backgroundAttachment = '';
    document.body.style.backgroundRepeat = '';
    document.body.style.minHeight = '';
    if (area) area.style.backgroundColor = '';
    applyThemeToRoot(activeTheme.value);
  }
};

const pageStyle = computed(() => {
  const color = activeTheme.value === 'premium'
    ? (liveCustomColor.value || eventCustomization.value?.primary_color || '#ff50a3')
    : (THEMES[activeTheme.value] || THEMES.quemimo)['--event-primary'];

  const t = activeTheme.value === 'premium'
    ? (() => { const { light, shadow } = hexToColorVars(color); return { '--event-primary': color, '--event-primary-light': light, '--event-primary-shadow': shadow }; })()
    : (() => { const th = THEMES[activeTheme.value] || THEMES.quemimo; return { '--event-primary': th['--event-primary'], '--event-primary-light': th['--event-primary-light'], '--event-primary-shadow': th['--event-primary-shadow'], '--event-bg': th['--event-bg'] }; })();

  return t;
});
const isOwner = computed(() => event.value?.is_owner === true);
const eventCustomization = computed(() => event.value?.customization ?? null);
const isWhiteLabel = computed(() => eventCustomization.value?.is_premium && eventCustomization.value?.white_label);
const hasCartItems = computed(() => {
  const ev = cartStore.getEvent(props.id);
  return ev ? ev.items.reduce((s, i) => s + i.quantity, 0) > 0 : false;
});

const windowWidth = ref(window.innerWidth);
const onResize = () => { windowWidth.value = window.innerWidth; };
onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));
const desktopColumns = computed(() => windowWidth.value >= 768 ? 3 : 2);

const handleThemeChange = async (newTheme) => {
  activeTheme.value = newTheme;
  themeSaved.value = false;
  themeSaveError.value = false;
  clearTimeout(saveTimer);
  // Tema premium só é salvo no servidor após pagamento confirmado
  if (newTheme === 'premium') return;
  saveTimer = setTimeout(async () => {
    themeSaving.value = true;
    try {
      await patchCustomization(props.id, { theme: newTheme });
      themeSaved.value = true;
      setTimeout(() => { themeSaved.value = false; }, 2000);
    } catch {
      themeSaveError.value = true;
    } finally {
      themeSaving.value = false;
    }
  }, 600);
};

const OWNER_ACTIVE_FLAG_CANDIDATES = [
  'owner_is_active',
  'ownerIsActive',
  'is_owner_active',
  'owner_active',
];

const readOwnerActiveFlag = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [
    payload,
    payload.event,
    payload.data,
    payload.owner,
    payload.event?.owner,
    payload.data?.event,
    payload.data?.owner,
    payload.data?.event?.owner,
  ].filter(Boolean);

  for (const source of candidates) {
    for (const field of OWNER_ACTIVE_FLAG_CANDIDATES) {
      if (typeof source[field] === 'boolean') {
        return source[field];
      }
    }
  }

  return null;
};

const OWNER_UNAVAILABLE_REASON_CANDIDATES = [
  'event_unavailable_reason',
  'eventUnavailableReason',
  'unavailable_reason',
  'unavailableReason',
];

const readUnavailableReason = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [
    payload,
    payload.event,
    payload.data,
    payload.data?.event,
  ].filter(Boolean);

  for (const source of candidates) {
    for (const field of OWNER_UNAVAILABLE_REASON_CANDIDATES) {
      if (typeof source[field] === 'string' && source[field].trim()) {
        return source[field].trim();
      }
    }
  }

  return null;
};

const isOwnerInactiveBusinessCase = (payload, status = null) => {
  const ownerIsActive = readOwnerActiveFlag(payload);
  if (ownerIsActive === false) return true;

  const unavailableReason = readUnavailableReason(payload);
  if (unavailableReason === 'OWNER_NOT_ACTIVE') return true;

  const message = String(payload?.message || payload?.error || '').toLowerCase();
  const mentionsActivation = /(ativ|active)/.test(message);
  const mentionsOwner = /(organizador|owner|dono|conta)/.test(message);

  if ((status === 403 || status === 422) && mentionsActivation && mentionsOwner) {
    return true;
  }

  return false;
};

const isEventUnavailableByOwnerActivation = computed(() => {
  if (!event.value) return false;
  if (event.value.is_owner === true) return false;
  return isOwnerInactiveBusinessCase(event.value);
});

// Banner do dono com evento em rascunho — bloqueio apenas por ativação de cadastro
const ownerDraftBanner = computed(() => {
  if (!event.value) return null;
  if (event.value.is_owner !== true) return null;
  if (event.value.status !== 'draft') return null;

  const ownerIsActive = readOwnerActiveFlag(event.value);
  const isActivationPending = authStore.isActivationPending || ownerIsActive === false;

  if (isActivationPending) {
    return {
      message: 'Seu evento já está pronto! 😊 Para receber presentes, falta concluir seu cadastro.',
      link: '/my-account/profile?ref=createaccount',
      linkText: 'Concluir cadastro',
    };
  }

  return null;
});

// Dados reativos
const event = ref(null);
const loading = ref(true);
const showErrorModal = ref(false);
const showConvite = ref(false);
const showPixFromList = ref(false);
const openCartSheet = ref(false);
const rsvpConfirmed = ref(false);

const getCachedRsvpCode = () => {
  const routeCode = String(route.query.code || '').trim().toUpperCase();
  if (routeCode) return routeCode;

  try {
    const d = JSON.parse(localStorage.getItem(`rsvp-confirmed-${props.id}`) || '{}');
    return String(d.code || '').trim().toUpperCase();
  } catch (_) {
    return '';
  }
};

const syncRsvpConfirmed = async () => {
  if (!props.id) return;
  const code = getCachedRsvpCode();
  if (!code) {
    rsvpConfirmed.value = false;
    return;
  }

  try {
    const { data } = await api.get(`/events/${props.id}/rsvp/${code}`);
    rsvpConfirmed.value = data.status === 'confirmed';
    if (!rsvpConfirmed.value) {
      localStorage.removeItem(`rsvp-confirmed-${props.id}`);
    }
  } catch (_) {
    rsvpConfirmed.value = false;
    localStorage.removeItem(`rsvp-confirmed-${props.id}`);
  }
};

const handleRsvpConfirmed = ({ code } = {}) => {
  rsvpConfirmed.value = true;
  if (props.id && code) {
    localStorage.setItem(`rsvp-confirmed-${props.id}`, JSON.stringify({ confirmed: true, code }));
  }
};

const conviteStorageKey = computed(() => `quemimo_convite_visto_${props.id}`);

const resolvedPrimaryColor = computed(() => {
  if (activeTheme.value === 'premium') {
    return liveCustomColor.value || event.value?.customization?.primary_color || '#ff50a3';
  }
  return (THEMES[activeTheme.value] || THEMES.quemimo)['--event-primary'];
});

const convitePhotos = computed(() => {
  const photos = event.value?.invite_photo_urls || [];
  if (photos.length > 0) return photos;
  // Fallback: usa a foto de capa do evento
  const cover = event.value?.baby_photo;
  return cover ? [cover] : [];
});

const openConvite = () => { showConvite.value = true; };

const closeConvite = () => {
  showConvite.value = false;
  localStorage.setItem(conviteStorageKey.value, '1');
  // Remove query param sem recarregar
  if (route.query.view === 'convite') {
    router.replace({ query: { ...route.query, view: undefined } });
  }
};

// Abre sidebar automaticamente se ?customize=1
watch(() => route.query.customize, (v) => {
  if (v === '1' && isOwner.value) showCustomizeSidebar.value = true;
}, { immediate: true });

// Abre convite quando ?view=convite muda (ex: clique em "Ver convite" no menu)
watch(() => route.query.view, (v) => {
  if (v === 'convite' && event.value) {
    showConvite.value = true;
  }
});

// const CACHE_TTL_MINUTES = 20; // Cache desativado

// --- Funções Auxiliares para Cache (desativadas) ---
// const setCachedData = (key, data, ttlMinutes) => { ... };
// const getCachedData = (key) => { ... };

// --- Funções da View ---

const scrollToGifts = () => {
  const el = document.getElementById('gift-list');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

const normalizeEventTypeName = (value) => {
  const typeName = String(value || '').trim();
  if (!typeName) return '';
  return typeName.charAt(0).toLowerCase() + typeName.slice(1);
};

const buildEventPageTitle = (eventPayload) => {
  const eventName = String(eventPayload?.name || '').trim();
  const eventTypeName = normalizeEventTypeName(eventPayload?.event_type_name || eventPayload?.eventTypeName);

  if (eventName && eventTypeName) {
    return formatPageTitle(`Lista de presentes de ${eventTypeName} de ${eventName}`);
  }

  if (eventName) {
    return formatPageTitle(`Lista de presentes de ${eventName}`);
  }

  return formatPageTitle('Lista de presentes');
};

const voltarHome = () => {
  router.back();
};

// ── Premium: funções ──────────────────────────────────────────
const handleLiveColorChanged = (color) => {
  liveCustomColor.value = color;
  if (activeTheme.value === 'premium') applyThemeToRoot('premium');
};

const handleLiveBgChanged = (url) => {
  // Propaga tanto a URL real quanto a limpeza (url='')
  liveBgImage.value = url || '';
  applyBodyBg(url);
};

const handleLiveBgOpacityChanged = (v) => {
  liveBgOpacity.value = v;
  applyBodyBg(liveBgImage.value);
};

const onPremiumSettingsSaved = ({ color, whiteLabel, bgImage }) => {
  if (color) liveCustomColor.value = color;
  activeTheme.value = 'premium';
  applyThemeToRoot('premium');
  if (whiteLabel !== undefined) eventStore.setWhiteLabel(whiteLabel);
  if (bgImage !== undefined) {
    liveBgImage.value = bgImage;
    applyBodyBg(bgImage);
  }
  // Atualiza store para que outras telas (checkout) reflitam a nova customização
  eventStore.setCustomization({
    customColor: color || liveCustomColor.value,
    bgImageURL:  bgImage !== undefined ? bgImage : liveBgImage.value,
    bgOpacity:   liveBgOpacity.value,
  });
};

const applyPremiumStateFromEvent = (loadedEvent) => {
  const cust = loadedEvent?.customization;
  if (!cust?.is_premium) return false;

  if (cust.primary_color) liveCustomColor.value = cust.primary_color;
  activeTheme.value = 'premium';
  applyThemeToRoot('premium');
  if (cust.background_image_url) {
    liveBgImage.value = cust.background_image_url;
    applyBodyBg(cust.background_image_url);
  }
  eventStore.setWhiteLabel(cust.white_label);
  eventStore.setCustomization({
    customColor: cust.primary_color || liveCustomColor.value || null,
    bgImageURL:  cust.background_image_url || liveBgImage.value || null,
    bgOpacity:   cust.background_opacity ?? liveBgOpacity.value,
  });
  return true;
};

const waitForPremiumActivation = async () => {
  premiumActivating.value = true;

  for (let attempt = 0; attempt < PREMIUM_ACTIVATION_POLL_ATTEMPTS; attempt++) {
    const eventRes = await api.get(`/events/${props.id}`);
    event.value = eventRes.data;
    eventStore.setEventMode(props.id, eventRes.data);

    if (applyPremiumStateFromEvent(eventRes.data)) {
      premiumSuccess.value = true;
      return;
    }

    if (attempt < PREMIUM_ACTIVATION_POLL_ATTEMPTS - 1) {
      await new Promise(resolve => setTimeout(resolve, PREMIUM_ACTIVATION_POLL_INTERVAL_MS));
    }
  }

  premiumError.value = 'Pagamento aprovado, mas a ativação do Premium ainda não foi confirmada. Aguarde alguns instantes e, se não liberar, fale com o suporte.';
};

const closePremiumModal = () => {
  showPremiumModal.value = false;
  premiumError.value = '';
  premiumActivating.value = false;
  premiumCardError.value = '';
  premiumCardName.value = '';
  premiumCardComplete.value = false;
  // Destroi o CardElement ao fechar
  if (_premiumCardEl) {
    _premiumCardEl.destroy();
    _premiumCardEl = null;
  }
};

const mountPremiumCardElement = async () => {
  if (!_premiumStripeKey) {
    const { data } = await api.get('/config/fees');
    _premiumStripeKey = String(data?.stripe_publishable_key || '').trim();
  }
  if (!_premiumStripe) {
    _premiumStripe = await loadStripe(_premiumStripeKey);
  }
  await nextTick();
  const mountNode = document.getElementById('premium-stripe-card-element');
  if (!mountNode || _premiumCardEl) return;
  const elements = _premiumStripe.elements();
  _premiumCardEl = elements.create('card', {
    hidePostalCode: true,
    disableLink: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#32355d',
        fontFamily: 'Nunito, sans-serif',
        '::placeholder': { color: '#9aa0b2' },
      },
      invalid: { color: '#dc3545' },
    },
  });
  _premiumCardEl.mount(mountNode);
  _premiumCardEl.on('change', (ev) => {
    premiumCardComplete.value = !!ev.complete;
    premiumCardError.value = ev.error?.message || '';
  });
};

const openPremiumModal = async () => {
  premiumSuccess.value = false;
  premiumError.value = '';
  premiumActivating.value = false;
  premiumCardError.value = '';
  premiumCardName.value = '';
  premiumCardComplete.value = false;
  showPremiumModal.value = true;
  // Monta o CardElement após o DOM aparecer
  await nextTick();
  await mountPremiumCardElement();
};

const handleActivatePremium = async () => {
  premiumError.value = '';
  premiumPaying.value = true;
  try {
    const { token, error } = await _premiumStripe.createToken(_premiumCardEl, {
      name: premiumCardName.value.trim(),
    });
    if (error) {
      premiumError.value = error.message;
      return;
    }

    const res = await createPremiumCheckout(props.id, token.id);
    const data = res.data;

    if (data.status === 'requires_action') {
      const actionResult = await _premiumStripe.handleNextAction({ clientSecret: data.client_secret });
      if (actionResult.error) {
        premiumError.value = actionResult.error.message;
        return;
      }
    }

    await waitForPremiumActivation();
  } catch (err) {
    premiumError.value = err?.response?.data?.message || err?.message || 'Erro ao processar pagamento.';
  } finally {
    premiumPaying.value = false;
    premiumActivating.value = false;
  }
};

// --- Ciclo de vida ---
onMounted(async () => {
  eventStore.setEventMode(props.id);

  try {
    const response = await api.get(`/events/${props.id}`);

    if (response.status > 200) {
      throw new Error(`Erro ao carregar o evento: Status HTTP ${response.status}`);
    }

    event.value = response.data;
    document.title = buildEventPageTitle(response.data);
    eventStore.setEventMode(props.id, response.data);
    cartStore.hydrateEvent(response.data);

    // Inicializa cor personalizada e background se evento tem valores salvos
    const cust = response.data?.customization;
    if (cust?.primary_color) {
      liveCustomColor.value = cust.primary_color;
    }
    if (cust?.background_opacity !== undefined && cust.background_opacity !== null) {
      liveBgOpacity.value = cust.background_opacity;
    }

    // Aplica tema vindo da API (DEPOIS de setar liveCustomColor para que premium use a cor correta)
    const theme = response.data?.customization?.theme;
    // 'premium' é um tema válido (modo cores personalizadas)
    activeTheme.value = (theme && (THEMES[theme] || theme === 'premium')) ? theme : 'quemimo';
    if (activeTheme.value) applyThemeToRoot(activeTheme.value);

    if (cust?.is_premium && cust?.background_image_url) {
      liveBgImage.value = cust.background_image_url;
      applyBodyBg(cust.background_image_url);
    }
    if (cust?.is_premium && cust?.primary_color) {
      document.documentElement.style.setProperty('--event-primary', cust.primary_color);
    }
    eventStore.setWhiteLabel(cust?.is_premium && cust?.white_label);
    await syncRsvpConfirmed();
    // Persiste customização premium no store para outras telas (checkout, etc)
    if (cust?.is_premium) {
      eventStore.setCustomization({
        customColor: cust.primary_color || null,
        bgImageURL:  cust.background_image_url || null,
        bgOpacity:   cust.background_opacity ?? 0.95,
      });
    }

    // Aguarda o browser pintar as CSS vars antes de remover o loader
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 200));

    // Abre sidebar se ?customize=1 e é dono
    if (route.query.customize === '1' && response.data?.is_owner === true) {
      showCustomizeSidebar.value = true;
    }

    // Lógica do convite digital: abre sempre na primeira visita
    const forceConvite = route.query.view === 'convite';
    const alreadySeen = localStorage.getItem(conviteStorageKey.value);
    if (forceConvite || !alreadySeen) {
      showConvite.value = true;
    }
  } catch (error) {
    console.error("Erro ao carregar o evento:", error);
    const status = error?.response?.status ?? null;
    const payload = error?.response?.data;

    if (isOwnerInactiveBusinessCase(payload, status)) {
      const fallbackEvent = payload?.event || payload?.data?.event || payload?.data || null;
      if (fallbackEvent && typeof fallbackEvent === 'object') {
        event.value = fallbackEvent;
        document.title = buildEventPageTitle(fallbackEvent);
        eventStore.setEventMode(props.id, fallbackEvent);
      } else {
        document.title = getBrandTitle();
        showErrorModal.value = true;
      }
    } else {
      document.title = getBrandTitle();
      showErrorModal.value = true;
    }
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  clearTimeout(saveTimer);
  // Remove as vars do :root ao sair — Cart/Checkout/Payment reaplicam via useEventTheme({ cleanup: true })
  removeThemeFromRoot();
  eventStore.reset();
});
</script>

<style scoped>
.page {
  --event-primary: #ff50a3;
  --event-bg: #f8f9fa;

  display: flex;
  flex-direction: column;
  background-color: transparent;
  height: 100%;
  width: 100%;
  max-width: 750px;
  transition: background-color 0.4s ease;
}

/* ── Botão de personalização ───────────────── */
.customize-fab {
  position: fixed;
  top: 0.5rem;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.8rem;
  background: #fff;
  border: 1.5px solid var(--event-primary, #ff50a3);
  border-radius: 6px;
  color: var(--event-primary, #ff50a3);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  z-index: 900;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  transition: background 0.15s, color 0.15s;
}

.customize-fab i {
  font-size: 0.75rem;
}

.customize-fab:hover {
  background: var(--event-primary, #ff50a3);
  color: #fff;
}

.customize-fab__label {
  font-size: 0.75rem;
}

/* ── FAB Pix ─────────────────────────────────────── */
.floating-pix-btn {
  position: fixed;
  right: 27px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid var(--event-primary, #ff50a3);
  color: var(--event-primary, #ff50a3);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999;
  box-shadow:
    0 3px 12px color-mix(in srgb, var(--event-primary, #ff50a3) 35%, transparent),
    0 1px 4px rgba(0, 0, 0, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  /* sem carrinho: fica na posição do carrinho */
  bottom: 24px;
  right: 20px;
}
/* com carrinho: sobe acima dele (24 + 58 + 10) */
.floating-pix-btn--has-cart {
  bottom: 92px;
  right: 27px;
}
/* com personalize (dono) sem carrinho: sobe acima do personalize (24 + 44 + 10) */
/* right: 20px herdado do default — alinha com o botão personalizar */
.floating-pix-btn--with-customize {
  bottom: 78px;
}
/* com personalize E carrinho: sobe acima dos dois (92 + 44 + 12) */
.floating-pix-btn--has-cart.floating-pix-btn--with-customize {
  bottom: 148px;
  right: 27px;
}
.floating-pix-btn:hover,
.floating-pix-btn:active {
  transform: scale(1.08);
}

/* ── Botão flutuante mobile (personalizar) ─────────── */
.floating-customize-btn {
  position: fixed;
  bottom: 94px; /* acima do carrinho (24 + 58 + 12) */
  right: 27px;  /* centralizado com o cart de 58px em right:20px */
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid var(--event-primary, #ff50a3);
  color: var(--event-primary, #ff50a3);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999;
  box-shadow:
    0 3px 12px color-mix(in srgb, var(--event-primary, #ff50a3) 35%, transparent),
    0 1px 4px rgba(0, 0, 0, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.floating-customize-btn--no-cart {
  bottom: 24px;
  right: 20px;
}

.floating-customize-btn:hover,
.floating-customize-btn:active {
  transform: scale(1.08);
  box-shadow:
    0 5px 16px color-mix(in srgb, var(--event-primary, #ff50a3) 50%, transparent),
    0 2px 6px rgba(0, 0, 0, 0.15);
}

.owner-draft-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  border-radius: 0.9rem;
  padding: 0.72rem 0.9rem;
  background: linear-gradient(135deg, #fff6e4 0%, #fff0d2 58%, #ffe8bf 100%);
  border: 1px solid #f6d392;
  box-shadow: 0 6px 16px rgba(50, 53, 93, 0.08);
  color: #4b4f68;
}

.owner-draft-warning i {
  margin-top: 0.1rem;
  color: #b56a00;
}

.owner-draft-warning .alert-link {
  color: #a74e1f;
  text-decoration: underline;
}

.owner-draft-warning .alert-link:hover {
  color: #8f4218;
}

@media (max-width: 767.98px) {
  .page {
    margin-top: 0rem;
    min-width: inherit;
  }
}

.event-content-shell {
  position: relative;  
  min-height: 300px;
}

.event-content-shell--unavailable .event-content-shell__content {
  pointer-events: none;
  user-select: none;
}

.event-unavailable-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1.5rem;
  background: linear-gradient(
    to top,
    rgba(248, 249, 250, 1) 0%,
    rgba(248, 249, 250, 1) 54%,
    rgba(248, 249, 250, 0.72) 78%,
    rgba(248, 249, 250, 0.18) 92%,
    rgba(248, 249, 250, 0) 100%
  );
  backdrop-filter: blur(3px);
  z-index: 3;
  pointer-events: all;
  user-select: none;
}

.event-unavailable-overlay__message {
  width: 100%;
  max-width: 560px;
  background: linear-gradient(180deg, #ffffff 0%, #fff8fc 100%);
  border: 1px solid #f3dfec;
  border-top: 3px solid #ff50a3;
  border-radius: 1rem;
  padding: 1.1rem 1.2rem 1rem;
  box-shadow: 0 10px 28px rgba(50, 53, 93, 0.12);
  text-align: center;
}

.event-unavailable-overlay__icon-wrap {
  display: flex;
  justify-content: center;
  margin-top: -2rem;
  margin-bottom: 0.35rem;
}

.event-unavailable-overlay__icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  background: linear-gradient(135deg, #ff50a3 0%, #e6408f 100%);
  box-shadow: 0 6px 18px rgba(255, 80, 163, 0.35);
  border: 3px solid #fff;
}

.event-unavailable-overlay__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #ff50a3;
}

.event-unavailable-overlay__title {
  font-size: 1.2rem;
  line-height: 1.3;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.35rem;
}

.event-unavailable-overlay__description {
  font-size: 0.92rem;
  color: #61677a;
  line-height: 1.5;
  margin-bottom: 0.95rem;
}

.event-unavailable-overlay__button {
  min-width: 160px;
  border: none;
  background: linear-gradient(135deg, #ff50a3 0%, #e6408f 100%);
  color: #fff;
  font-weight: 700;
  border-radius: 0.7rem;
  padding: 0.55rem 1rem;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.28);
  transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.event-unavailable-overlay__button:hover {
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 80, 163, 0.35);
}

@media (max-width: 767.98px) {
  .event-unavailable-overlay {
    padding: 0.9rem;
    align-items: flex-end;
  }

  .event-unavailable-overlay__message {
    padding: 1rem 0.9rem 0.9rem;
    border-radius: 0.85rem;
  }

  .event-unavailable-overlay__icon-wrap {
    margin-top: -1.8rem;
  }

  .event-unavailable-overlay__icon {
    width: 44px;
    height: 44px;
    font-size: 0.92rem;
  }

  .event-unavailable-overlay__eyebrow {
    font-size: 0.68rem;
  }

  .event-unavailable-overlay__title {
    font-size: 1rem;
  }

  .event-unavailable-overlay__description {
    font-size: 0.88rem;
    margin-bottom: 0.7rem;
  }

  .event-unavailable-overlay__button {
    width: 100%;
  }
}

/* ── Premium Modal ──────────────────────────────────────────── */
.premium-modal { padding: 0.25rem 0 0.5rem; }

.premium-modal__header { text-align: center; margin-bottom: 1.2rem; }

.premium-modal__crown {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #fff;
  border-radius: 50%;
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 16px rgba(217,119,6,0.35);
}

.premium-modal__title {
  font-size: 1.2rem;
  font-weight: 800;
  color: #32355d;
  margin: 0 0 0.35rem;
}

.premium-modal__subtitle {
  font-size: 0.88rem;
  color: #61677a;
  margin: 0;
}

.premium-modal__field { margin-bottom: 1rem; }

.premium-modal__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.4rem;
}

.premium-modal__stripe-element {
  border: 1.5px solid #e5e7eb;
  border-radius: 0.65rem;
  padding: 0.75rem 0.8rem;
  background: #fff;
  transition: border-color 0.18s;
  min-height: 44px;
}
.premium-modal__stripe-element:focus-within { border-color: var(--event-primary, #ff50a3); }

.premium-modal__stripe-error { font-size: 0.78rem; color: #ef4444; margin-top: 0.35rem; }

.premium-modal__input {
  width: 100%;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.65rem;
  padding: 0.65rem 0.8rem;
  font-size: 0.95rem;
  color: #32355d;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
}
.premium-modal__input:focus { border-color: var(--event-primary, #ff50a3); }

.premium-modal__error {
  font-size: 0.82rem;
  color: #ef4444;
  margin-bottom: 0.75rem;
}

.premium-modal__pay-btn {
  width: 100%;
  padding: 0.7rem;
  border: none;
  border-radius: 0.65rem;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.18s, transform 0.18s;
  box-shadow: 0 3px 12px rgba(217,119,6,0.35);
}
.premium-modal__pay-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.premium-modal__pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.premium-modal__secure {
  text-align: center;
  font-size: 0.72rem;
  color: #9ca3af;
  margin-top: 0.6rem;
  margin-bottom: 0;
}

.premium-modal__success { text-align: center; padding: 1rem 0 0.5rem; }

.premium-modal__success-icon {
  font-size: 2.8rem;
  color: #22c55e;
}

.premium-modal__close-btn {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.55rem 1.5rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #fff;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
}

/* ── Rodapé de marca ── */
.event-brand-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 1.25rem 0 0.75rem;
  font-size: 0.8rem;
  color: #adb5bd;
}
.event-brand-footer strong {
  color: #ff50a3;
  font-weight: 700;
}
.event-brand-footer__heart {
  color: #ff50a3;
  font-size: 0.7rem;
}
</style>